import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { cva } from 'class-variance-authority'

import { Icon } from '@/components/icon'
import { cn } from '@/infra'

type CardVariant = 'default' | 'completed' | 'optional'

type CardContextType = {
  variant: CardVariant
}

const CardContext = createContext<CardContextType>({ variant: 'default' })

const cardVariant = cva('flex h-11 w-11 items-center justify-center rounded-full', {
  variants: {
    variant: {
      default: 'bg-orange-50 text-red-800',
      completed: 'bg-green-50 text-green-800',
      optional: 'bg-slate-50 text-gray-700',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

type CardRootProps = {
  children: ReactNode
  variant: CardVariant
  className?: string
}

function CardRoot({ children, variant = 'default', className }: Readonly<CardRootProps>) {
  const value = useMemo(() => ({ variant }), [variant])
  return (
    <CardContext.Provider value={value}>
      <div
        className={cn(
          'bg-foreground flex min-h-72 min-w-full flex-col gap-4 rounded-lg border p-6 shadow-sm md:w-85 md:min-w-85',
          className,
        )}
        data-testid="card-etapa-inicial"
      >
        {children}
      </div>
    </CardContext.Provider>
  )
}

function CardIcon() {
  const { variant } = useContext(CardContext)
  const completed = variant === 'completed'

  return (
    <div data-testid="badge" className={cn(cardVariant({ variant }))}>
      <Icon className="h-6 w-6" name={completed ? 'circle-check' : 'circle-alert'} />
    </div>
  )
}

type CardContentProps = {
  label: string
  description: string
  children?: ReactNode
  className?: string
}

function CardContent({ label, description, children, className }: Readonly<CardContentProps>) {
  return (
    <div className="grid gap-4">
      <CardIcon />
      <div className={cn('h-27 truncate', className)} data-testid="card-etapa-inicial-header">
        <h2 className="mb-1 text-2xl font-normal text-sky-950">{label}</h2>
        <p className="text-sm text-neutral-700">{description}</p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  )
}

type CardActionProps = {
  children: ReactNode
}

function CardAction({ children }: Readonly<CardActionProps>) {
  return <div className="h-17">{children}</div>
}

export const CardEtapaInicial = Object.assign(CardRoot, {
  Content: CardContent,
  Action: CardAction,
})
